import React, { useEffect, useState } from 'react';
import SinglePowerApp from './SinglePowerApp';
import TileGroup from './TileGroup';
import { AppType, FlowState, PowerAppsScope } from "../../GlobalConst/globalEnums";
import { ActionButton, ChoiceGroup, Pivot, PivotItem, Spinner, SpinnerSize, Toggle, TooltipHost } from '@fluentui/react';
import { addPivotItemKeyToUrl, getPivotItemKeyFromUrl, mapEnumToOptionsWithValues } from '../../helpers/HelperMethods';
import SinglePowerAppSolution from './SinglePowerAppSolution';
import PowerAppEnvironments from './PowerAppEnvironments';
import PowerPlatformSolutionsList from '../Administration/PowerPlatform/PowerPlatformSolutionsList';
import PowerPlatformItemsList from '../Administration/PowerPlatform/PowerPlatformItemsList';
import { useSelector } from 'react-redux';

const MyPowerPlatform = ({apps, flows, agents, solutions, environments, getCallback, getPageNavi, isLoading, parentPivotKey, onFilterToggleChange }) => {

    const showAgents = !useSelector((state) => state.app.appConfiguration.limitAgentsToPilotUsers) || useSelector((state) => state.user.isPowerPlatformPilotUser)

    const [isToggleShowAppsFromSolutions, setIsToggleShowAppsFromSolutions] = useState()
    const [isToggleShowFlowsFromSolutions, setIsToggleShowFlowsFromSolutions] = useState()
    const [filterScopeSolutions, setFilterScopeSolutions] = useState()
    const [filterScopeApps, setFilterScopeApps] = useState()
    const [filterScopeFlows, setFilterScopeFlows] = useState()
    const [filterScopeAgents, setFilterScopeAgents] = useState()
    const [isToggleShowSupportingSolutions, setIsToggleShowSupportingSolutions] = useState()
    const [isSolutionsListView, setIsSolutionsListView] = useState(false)
    const [isPowerAppsListView, setIsPowerAppsListView] = useState(false)
    const [isFlowsListView, setIsFlowsListView] = useState(false)
    const [isAgentsListView, setIsAgentsListView] = useState(false)
    
    useEffect(() => {
        if (isToggleShowAppsFromSolutions !== undefined)
            fetchPowerApps()
    }, [isToggleShowAppsFromSolutions])
    
    useEffect(() => {
        if (isToggleShowFlowsFromSolutions !== undefined)
            fetchFlows()
    }, [isToggleShowFlowsFromSolutions])
    
    useEffect(() => {
        if (isToggleShowSupportingSolutions !== undefined)
            fetchSolutions()
    }, [isToggleShowSupportingSolutions])

    useEffect(() => {
        if (filterScopeSolutions !== undefined)
            fetchSolutions()
    }, [filterScopeSolutions])
    
    useEffect(() => {
        if (filterScopeApps !== undefined)
            fetchPowerApps()
    }, [filterScopeApps])

    useEffect(() => {
        if (filterScopeFlows !== undefined)
            fetchFlows()
    }, [filterScopeFlows])

    useEffect(() => {
        if (filterScopeAgents !== undefined)
            fetchAgents()
    }, [filterScopeAgents])

    const fetchPowerApps = () => {
        onFilterToggleChange('owner', 'powerApps', 'PowerAppsPanel', `type=powerApp&${getInOutSolutionsFilter(isToggleShowAppsFromSolutions)}${getScope(filterScopeApps, true)}`)
    }

    const fetchFlows = () => {
        onFilterToggleChange('owner', 'flows', 'PowerAppsPanel', `type=flow&${getInOutSolutionsFilter(isToggleShowFlowsFromSolutions)}${getScope(filterScopeFlows, true)}`)
    }

    const fetchAgents = () => {
        onFilterToggleChange('owner', 'agents', 'PowerAppsPanel', `type=agent${getScope(filterScopeAgents, true)}`)
    }

    const fetchSolutions = () => {
        onFilterToggleChange('owner', 'powerAppSolutions', 'PowerAppSolutionsPanel', `isTileView=true&${getSupportingSolutionsFilter(isToggleShowSupportingSolutions)}${getScope(filterScopeSolutions, true)}`)
    }

    const getScope = (filterScope, includeAmpersand = false) => {
        return filterScope !== 'all' && filterScope !== undefined
            ? `${includeAmpersand ? '&' : ''}scope=${filterScope}`
            : ''
    }

    const getInOutSolutionsFilter = (toggle) => {
        return `fromSolutions=${!!toggle}`
    }

    const getSupportingSolutionsFilter = (toggle) => {
        return `getSupportingSolutions=${!!toggle}`
    }

    const appTypes = [
        {
            value: AppType.portal,
            label: 'Portal Apps'
        },
        {
            value: AppType.canvas,
            label: 'Canvas Apps'
        },
        {
            value: AppType.modelDriven,
            label: 'Model-Driven Apps'
        },
        {
            value: AppType.sharePointFormApp,
            label: 'SharePoint Form Apps'
        },
        {
            value: AppType.customPage,
            label: 'Custom Pages'
        },
        {
            value: AppType.componentLibrary,
            label: 'Component Libraries'
        },
        {
            value: AppType.codeApp,
            label: 'Code Apps'
        }
    ]

    const flowStates = [
        {
            value: FlowState.started,
            label: 'Started'
        },
        {
            value: FlowState.stopped,
            label: 'Stopped'
        },
        {
            value: FlowState.suspended,
            label: 'Suspended'
        },
    ]

    const solutionHeaderLabelStyles = {
        fontSize: '16px',
    }

    const inOutSolutions = [
        {
            value: true,
            label: 'Inside Solutions'
        },{
            value: false,
            label: 'Outside Solutions'
        },
    ]

    const getSolutionGroupedTiles = (items, showLabel, callback) => {
        return items.length && inOutSolutions.map((s, k) => 
            <TileGroup
                key={k}
                groupBy={s.label}
                labelStyles={solutionHeaderLabelStyles}
                hideLabel={!showLabel}
                elements={items.filter(c => c.isPartOfSolution === s.value).map(p => 
                    <SinglePowerApp
                        key={p.id}
                        app={p}
                        callBack={callback}
                        solutionCallBack={item => getCallback(item, 'powerAppSolutions')}
                    />)
                }
            />
        )
    }

    const appsToDisplay = appTypes.map((x, i) =>
        <TileGroup
            key={i}
            groupBy={x.label}
            itemStyles={{}}
            elements={getSolutionGroupedTiles(apps.results.filter(c => c.appType === x.value), x.value !== AppType.sharePointFormApp, item => getCallback(item, 'powerApps'))}
        />
    )    

    const flowsToDisplay = flowStates.map((x, i) =>
        <TileGroup
            key={i}
            groupBy={x.label}
            itemStyles={{}}
            elements={getSolutionGroupedTiles(flows.results.filter(c => c.state === x.value), true, item => getCallback(item, 'flows'))}
        />
    )

     const agentsToDisplay = 
        <TileGroup
            elements={agents.results.map((x, i) =>
                <SinglePowerApp
                    key={x.id}
                    app={x}
                    callBack={item => getCallback(item, 'agents')}
                />
            )}
        />

    const solutionsToDisplay = 
        <TileGroup
            elements={solutions.results.map(p => 
                <SinglePowerAppSolution
                    key = {p.id}
                    solution = {p}
                    callBack = {item => getCallback(item, 'powerAppSolutions')}
                />
            )}
        />

    const ScopeChoiceFilter = ({selectedKey, onChange}) => 
        <ChoiceGroup 
            selectedKey={selectedKey} 
            defaultSelectedKey='all' 
            options={getFilterScopeOptions()} 
            onChange={(e, v) => onChange(v.key)} 
            label='Scope' 
            styles={choiceFilterStyles} 
        />

    const spinner = <Spinner style={{margin: '200px'}} size={SpinnerSize.large} />;
    const powerAppsNavi = getPageNavi('powerApps', 'owner', 'PowerAppsPanel', `type=powerApp&${getInOutSolutionsFilter(isToggleShowAppsFromSolutions)}${getScope(filterScopeApps, true)}`);
    const flowsNavi = getPageNavi('flows', 'owner', 'PowerAppsPanel', `type=flow&${getInOutSolutionsFilter(isToggleShowFlowsFromSolutions)}${getScope(filterScopeFlows, true)}`);
    const agentsNavi = getPageNavi('agents', 'owner', 'PowerAppsPanel', `type=agent${getScope(filterScopeAgents, true)}`);
    const solutionsNavi = getPageNavi('powerAppSolutions', 'owner', 'PowerAppSolutionsPanel');

    const defaultSelectedKey = environments.allTotalCount !== undefined && getPivotItemKeyFromUrl(environments.allTotalCount > 0 ? 'MyEnvironments' : 'MySolutions', parentPivotKey);
    const getFilterScopeOptions = () => {
        const options = [{key: 'all', text: 'All'}, ...mapEnumToOptionsWithValues(PowerAppsScope)]
        return options.map(x => ({...x, styles: choiceFilterOptionStyles}))
    }

    const toggleDisplayItemsFromSolutionsApps = <Toggle checked={isToggleShowAppsFromSolutions} label='Display Power Apps from inside solutions' onText='Show' offText='Hide' onChange={(e, v) => setIsToggleShowAppsFromSolutions(v)} styles={toggleStyles} />
    const toggleDisplayItemsFromSolutionsFlows = <Toggle checked={isToggleShowFlowsFromSolutions} label='Display Flows from inside solutions' onText='Show' offText='Hide' onChange={(e, v) => setIsToggleShowFlowsFromSolutions(v)} styles={toggleStyles} />
    const toggleDisplaySupportingSolutions = <Toggle checked={isToggleShowSupportingSolutions} label='Display supporting solutions' onText='Show' offText='Hide' onChange={(e, v) => setIsToggleShowSupportingSolutions(v)} styles={toggleStyles} />
    
    const choiceFilterScopeSolutions = <ScopeChoiceFilter selectedKey={filterScopeSolutions} onChange={setFilterScopeSolutions} />
    const choiceFilterScopeApps = <ScopeChoiceFilter selectedKey={filterScopeApps} onChange={setFilterScopeApps} />
    const choiceFilterScopeFlows = <ScopeChoiceFilter selectedKey={filterScopeFlows} onChange={setFilterScopeFlows} />
    const choiceFilterScopeAgents = <ScopeChoiceFilter selectedKey={filterScopeAgents} onChange={setFilterScopeAgents} />

    const ChangeViewIcons = ({setStateFunction}) =>
        <div style={{display: 'flex', marginLeft: 'auto'}}>
            <ActionButton styles={switchViewStyles} iconProps={{iconName: 'tiles'}} onClick={() => setStateFunction(false)}><TooltipHost content='Show in tile view'>Tile View</TooltipHost></ActionButton> 
            <ActionButton styles={switchViewStyles} iconProps={{iconName: 'bulletedList'}} onClick={() => setStateFunction(true)}><TooltipHost content='Show in list view'>List View</TooltipHost></ActionButton> 
        </div>

    return(
        <div style={{width: '100%'}}>
            {
                 isLoading.powerAppEnvironments
                 ? spinner
                 :
                    <Pivot styles={pivotStyles} defaultSelectedKey={defaultSelectedKey} onLinkClick={addPivotItemKeyToUrl(parentPivotKey)}>
                        {
                            environments.allTotalCount > 0 &&
                            <PivotItem itemKey='MyEnvironments' headerText='Environments' itemCount={environments.allTotalCount}>
                                {
                                    isLoading.powerAppEnvironments
                                        ? spinner
                                        : <div>
                                            <PowerAppEnvironments defaultEnvironments={environments} parentPivotKey={`${parentPivotKey}/MyEnvironments`} />
                                        </div>
                                }
                            </PivotItem>
                        }
                        <PivotItem itemKey='MySolutions' headerText='Solutions' itemCount={solutions.allTotalCount}>
                            <div style={toggleContainerStyles}>
                                {!isSolutionsListView && toggleDisplaySupportingSolutions}
                                {!isSolutionsListView && choiceFilterScopeSolutions}
                                <ChangeViewIcons setStateFunction={setIsSolutionsListView} />
                            </div>
                            {
                                isSolutionsListView
                                ? <PowerPlatformSolutionsList filterBy='owner' />  
                                : isLoading.powerAppSolutions
                                    ? spinner
                                    : <div>
                                        {solutionsToDisplay}
                                        {solutionsNavi}
                                    </div>
                            }
                        </PivotItem>
                        <PivotItem itemKey='MyPowerApps' headerText='Power Apps' itemCount={apps.allTotalCount}>
                            <div style={toggleContainerStyles}>
                                {!isPowerAppsListView && toggleDisplayItemsFromSolutionsApps}
                                {!isPowerAppsListView && choiceFilterScopeApps}
                                <ChangeViewIcons setStateFunction={setIsPowerAppsListView} />
                            </div>
                            {
                                isPowerAppsListView
                                ? <PowerPlatformItemsList filterBy='owner' type='powerApp' />  
                                : isLoading.powerApps
                                    ? spinner
                                    : <div>
                                        {appsToDisplay}
                                        {powerAppsNavi}
                                    </div>
                            }
                        </PivotItem>
                        <PivotItem itemKey='MyFlows' headerText='Power Automate' itemCount={flows.allTotalCount}>   
                            <div style={toggleContainerStyles}>
                                {!isFlowsListView && toggleDisplayItemsFromSolutionsFlows}
                                {!isFlowsListView && choiceFilterScopeFlows}
                                <ChangeViewIcons setStateFunction={setIsFlowsListView} />
                            </div>
                            {
                                isFlowsListView
                                ? <PowerPlatformItemsList filterBy='owner' type='flow' />  
                                : isLoading.flows
                                    ? spinner
                                    : <div>
                                        {flowsToDisplay}
                                        {flowsNavi}
                                    </div>
                            }
                        </PivotItem>   
                        {
                            showAgents &&
                            <PivotItem itemKey='MyAgents' headerText='Copilot Studio Agents' itemCount={agents.allTotalCount}>   
                                <div style={toggleContainerStyles}>
                                    {!isAgentsListView && choiceFilterScopeAgents}
                                    <ChangeViewIcons setStateFunction={setIsAgentsListView} />
                                </div>
                                {
                                    isAgentsListView
                                    ? <PowerPlatformItemsList filterBy='owner' type='agent' />  
                                    : isLoading.agents
                                        ? spinner
                                        : <div>
                                            {agentsToDisplay}
                                            {agentsNavi}
                                        </div>
                                }
                            </PivotItem> 
                        }
                    </Pivot>
            }
        </div>
    )
}

const toggleContainerStyles = {
    display: 'flex'
}

const toggleStyles = {
    root: {
        margin: '-10px 40px 20px 0'
    }
}

const pivotStyles = {
    root: {
        marginBottom: 20
    },
    linkContent: {
        fontSize: 16,
        flex: 'auto'
    }
}

const choiceFilterStyles = {
    root: {
        margin: '-10px 40px 20px 0'
    },
    flexContainer: {
        display: 'flex'
    }
}

const choiceFilterOptionStyles = {
    root: {
        marginTop: 0,
        marginRight: 20
    }
}

const switchViewStyles = {
    root: {
        fontSize: 16
    }
}

export default MyPowerPlatform


// WEBPACK FOOTER //
// ./src/components/ManageCenter/MyPowerPlatform.js